import React, { useEffect } from 'react'
import type { BettingType } from '@mobi/betslip/types'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import {
  state$ as userState$,
  UserAccountState,
  UserAccountStateRecord,
} from '@core/State/UserAccount/userAccountDriver'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { fetchCampaignsAsync } from '@core/State/UserAccount/async-signals'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { CampaignStyled, CampaignTitleStyled, RemoveCampaignButtonStyled } from './Campaigns.styles'
import { isCampaignRedeemableWithBettingType } from '@mobi/betslip/helpers/campaignHelper'

export type ActiveCampaignsProps = Pick<UserAccountState, 'isLoggedIn' | 'activeCampaigns'> & {
  shouldDisplay: boolean
  bettingType: BettingType | null
  handleRemoveBonusCashButtonClick: () => void
  showRemoveButton: boolean
}

export const ActiveCampaignsComponent = ({
  isLoggedIn,
  activeCampaigns,
  shouldDisplay,
  bettingType,
  showRemoveButton,
  handleRemoveBonusCashButtonClick,
}: ActiveCampaignsProps) => {
  useEffect(() => {
    if (isLoggedIn) fetchCampaignsAsync()
  }, [isLoggedIn])

  const campaigns: Campaign[] | null = activeCampaigns
    ? activeCampaigns
        .filter(
          campaign =>
            campaign.rewardType != 'BonusBetReward' &&
            (isCampaignRedeemableWithBettingType(campaign, bettingType) ||
              bettingType === 'mystery-quick-pick')
        )
        .toArray()
    : null

  if (!isLoggedIn || !campaigns || !campaigns.length || !shouldDisplay) {
    return null
  }

  const title = `$${campaigns[0].remainingAmount} - ${campaigns[0].title}`

  return (
    <CampaignStyled>
      <Grid>
        <GridCell width='3rem' valign='middle'>
          <Icon type='infoInvert' />
        </GridCell>
        <GridCell valign='middle'>
          <CampaignTitleStyled>{title}</CampaignTitleStyled>
        </GridCell>
        <GridCell width='3rem' valign='middle' align='right'>
          {showRemoveButton && (
            <RemoveCampaignButtonComponent
              onClick={handleRemoveBonusCashButtonClick}
              isDisabled={false}
            />
          )}
        </GridCell>
      </Grid>
    </CampaignStyled>
  )
}

export const Campaigns = observeImmutable<
  UserAccountStateRecord,
  {
    shouldDisplay: boolean
    bettingType: BettingType | null
    handleRemoveBonusCashButtonClick: () => void
    showRemoveButton: boolean
  }
>(
  userState$,
  ({
    record,
    shouldDisplay,
    bettingType,
    handleRemoveBonusCashButtonClick,
    showRemoveButton: showTrashButton,
  }) => {
    return (
      <ActiveCampaignsComponent
        isLoggedIn={record.isLoggedIn}
        activeCampaigns={record.activeCampaigns}
        shouldDisplay={shouldDisplay}
        bettingType={bettingType}
        handleRemoveBonusCashButtonClick={handleRemoveBonusCashButtonClick}
        showRemoveButton={showTrashButton}
      />
    )
  }
)

// ===============
// Local Component
// ===============

const RemoveCampaignButtonComponent: React.FC<{
  onClick: () => void
  isDisabled: boolean
}> = ({ onClick, isDisabled }) => (
  <RemoveCampaignButtonStyled onClick={onClick} disabled={isDisabled}>
    <Icon type='minusCircled' title='Remove' size='1.8rem' />
  </RemoveCampaignButtonStyled>
)
